import styled from '@emotion/styled';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { acceptPermission, refusePermission } from 'modules/notifications/actions';
import { getCurrentUserId } from 'modules/users/selectors/currentUser';

import Button from 'ui/base/Button';
import logo from 'ui/base/images/notification.gif';
import Modal from 'ui/base/Modal';
import BaseTooltip from 'ui/base/Tooltip';

const Tooltip = styled(BaseTooltip)`
  font-style: italic;
  font-weight: 500;
  text-decoration: underline dashed rgba(91, 103, 112, 0.5);
`;

const Logo = styled.img`
  width: 50px;
  height: 50px;
  margin-right: 16px;
`;

const Title = styled.div`
  display: flex;
`;

const Permission = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const userId = useSelector(getCurrentUserId);

  const handleSubmit = () => {
    dispatch(acceptPermission());
  };

  const handleCancel = () => {
    dispatch(refusePermission(userId));
  };

  const tooltipRender = React.useCallback(
    chunks => (
      <Tooltip
        title={intl.formatMessage({
          id: 'notifications.permission.tooltip',
          defaultMessage: 'If you change your mind, you will still be able to deactivate the feature later on.',
        })}
      >
        {chunks}
      </Tooltip>
    ),
    [intl]
  );

  return (
    <Modal
      title={
        <Title>
          <Logo src={logo} alt="Notifications" />
          <FormattedMessage
            id="notifications.permission.title"
            defaultMessage="Receive real-time notifications from Fleetback!"
          />
        </Title>
      }
      footer={[
        <Button
          key="back"
          onClick={handleCancel}
          data-cy="cancel_permissions"
          analytics={{ category: 'Notifications', action: 'Permission refused' }}
        >
          <FormattedMessage id="notifications.permission.refuse" defaultMessage="No, thanks" />
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          data-cy="accept_permissions"
          analytics={{ category: 'Notifications', action: 'Permission accepted' }}
        >
          <FormattedMessage id="notifications.permission.accept" defaultMessage="Got it" />
        </Button>,
      ]}
      mask={{ closable: false }}
      open
    >
      <FormattedMessage
        id="notifications.permission.content"
        defaultMessage="Your browser will now ask you to enable Fleetback notifications. By doing so, Fleetback will automatically synchronize with live information sent by your customers, or other Fleetback users."
        tagName="p"
      />
      <FormattedMessage
        id="notifications.permission.subContent"
        defaultMessage="You will be able to <tooltip>configure</tooltip> the notifications you receive in your settings afterwards."
        values={{
          tooltip: tooltipRender,
        }}
      />
    </Modal>
  );
};

export default Permission;
